import { GRADE_OPTIONS } from '../../utils/signupEligibility';

/**
 * Who a round is for.
 *
 * Left empty, a round is offered to every family — which is how every round behaved before
 * this existed, and is right for something like a whole-school open evening. Filled in, it is
 * the difference between a TK/K conference reaching thirty families and reaching all of them:
 * the parent dashboard pins an open round to the top of "Needs you", so an untargeted round
 * tells a G3 family, by name, that their child has no slot.
 *
 * Grades are the only thing worth setting per round. The other half of the rule — that the
 * child holds an active after-school enrollment — is constant: BHCS runs one after-school
 * programme, and a parent-teacher meeting is about the teacher a child actually has. It was a
 * column briefly, and it was a setting nobody would ever set differently.
 */
export function SignupAudienceEditor({
  targetGrades,
  onChange,
}: {
  targetGrades: string[];
  onChange: (patch: { targetGrades?: string[] }) => void;
}) {
  const toggle = (list: string[], value: string): string[] =>
    list.includes(value) ? list.filter(item => item !== value) : [...list, value];

  const chip = (checked: boolean) =>
    `cursor-pointer rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
      checked
        ? 'border-blue-300 bg-blue-100 text-blue-800'
        : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
    }`;

  const everyone = targetGrades.length === 0;

  return (
    <div className="space-y-3">
      <div>
        <h4 className="text-sm font-medium text-gray-700">Who it is for</h4>
        <p className="text-xs text-gray-500 mt-0.5">
          Pick the grades this round is for. Leave it empty and every family is offered it.
          Either way, only children with an active after-school enrollment are included.
        </p>
      </div>

      <div>
        <label className="block text-xs text-gray-600 mb-1.5">Grades</label>
        <div className="flex flex-wrap gap-1.5">
          {GRADE_OPTIONS.map(grade => (
            <label key={grade} className={chip(targetGrades.includes(grade))}>
              <input
                type="checkbox"
                className="sr-only"
                checked={targetGrades.includes(grade)}
                onChange={() => onChange({ targetGrades: toggle(targetGrades, grade) })}
              />
              {grade}
            </label>
          ))}
        </div>
      </div>

      <p className={`text-sm ${everyone ? 'text-amber-700' : 'text-gray-600'}`}>
        {everyone
          ? 'Every after-school family will be prompted to book.'
          : `Offered to after-school children in ${targetGrades.join(', ')}.`}
      </p>
    </div>
  );
}
